/**
 * Invoice Frontend Types
 * TypeScript types for invoice management on the client side
 */

/**
 * Product Types
 */
export type ProductType = "goods" | "service";

/**
 * Invoice Type
 */
export type InvoiceType = "standard" | "free";

export const INVOICE_TYPE_OPTIONS = [
  { value: "standard" as InvoiceType, label: "Standard-Rechnung" },
  { value: "free" as InvoiceType, label: "Freirechnung" },
] as const;

/**
 * Invoice Status
 */
export type InvoiceStatus = "draft" | "sent" | "paid" | "cancelled" | "deleted";

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Entwurf",
  sent: "Versendet",
  paid: "Bezahlt",
  cancelled: "Storniert",
  deleted: "Gelöscht",
} as const;

export const PRODUCT_TYPE_OPTIONS = [
  { value: "goods", label: "Waren" },
  { value: "service", label: "Dienstleistung" },
] as const;

/**
 * VAT Rate Options
 */
export const VAT_RATE_OPTIONS = [
  { value: "0", label: "0%" },
  { value: "7", label: "7%" },
  { value: "19", label: "19%" },
] as const;

/**
 * Unit Options
 */
export const UNIT_OPTIONS = [
  { value: "cm", label: "cm" },
  { value: "m", label: "m" },
  { value: "km", label: "km" },
  { value: "kg", label: "kg" },
  { value: "h", label: "h" },
  { value: "Stk.", label: "Stk." },
] as const;

/**
 * Payment Terms Presets
 */
export const PAYMENT_TERMS_OPTIONS = [
  { value: 0, label: "Sofort fällig" },
  { value: 7, label: "7 Tage" },
  { value: 14, label: "14 Tage" },
  { value: 30, label: "30 Tage" },
  { value: 60, label: "60 Tage" },
  { value: 90, label: "90 Tage" },
  { value: -1, label: "Benutzerdefiniert" },
] as const;

/**
 * Invoice Item
 */
export interface InvoiceItem {
  id?: string;
  description: string;
  productType: ProductType;
  itemDate: Date | string;
  quantity: number | string;
  unit: string;
  unitPrice: number | string;
  amount?: number | string;
  position?: number;
}

/**
 * Invoice
 */
export interface Invoice {
  id: string;
  userId: string;
  customerId?: string | null;
  invoiceNumber: string;
  invoiceType: InvoiceType;
  invoiceDate: Date | string;
  paymentTerms: number;
  dueDate: Date | string;
  purchaseOrderNumber?: string | null;
  leitwegId?: string | null;
  message?: string | null;
  vatRate: string;
  isSmallBusiness: string;
  smallBusinessNote?: string | null;
  subtotal: string;
  taxAmount: string;
  total: string;
  status: InvoiceStatus;
  sentAt?: Date | string | null;
  paidAt?: Date | string | null;
  cancelledAt?: Date | string | null;
  cancelledById?: string | null;
  cancellationReason?: string | null;
  originalInvoiceId?: string | null;
  deletedAt?: Date | string | null;
  deletedById?: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  items?: InvoiceItem[];
}

/**
 * Create Invoice Input
 */
export interface CreateInvoiceInput {
  customerId: string;
  invoiceType?: InvoiceType;
  invoiceNumber?: string;
  invoiceDate: Date | string;
  paymentTerms: number;
  dueDate: Date | string;
  purchaseOrderNumber?: string;
  leitwegId?: string;
  message?: string;
  vatRate: string;
  isSmallBusiness: string;
  smallBusinessNote?: string;
  items: Omit<InvoiceItem, "id" | "amount">[];
}

/**
 * Update Invoice Input
 */
export interface UpdateInvoiceInput {
  customerId?: string;
  invoiceType?: InvoiceType;
  invoiceNumber?: string;
  invoiceDate?: Date | string;
  paymentTerms?: number;
  dueDate?: Date | string;
  purchaseOrderNumber?: string;
  leitwegId?: string;
  message?: string;
  vatRate?: string;
  isSmallBusiness?: string;
  smallBusinessNote?: string;
  items?: Omit<InvoiceItem, "id" | "amount">[];
}

/**
 * Invoice Query Parameters
 */
export interface InvoiceQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search?: string;
  startDate?: string;
  endDate?: string;
  customerId?: string;
  includeDeleted?: boolean;
  invoiceType?: InvoiceType;
}

/**
 * Invoice List Response
 */
export interface InvoiceListResponse {
  data: Invoice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Invoice Stats
 */
export interface InvoiceStats {
  totalCount: number;
  totalAmount: string;
  overdueCount: number;
  thisMonthCount: number;
}

/**
 * Invoice History Entry
 */
export interface InvoiceHistoryEntry {
  id: string;
  invoiceId: string;
  userId: string;
  action: string;
  oldStatus?: string | null;
  newStatus?: string | null;
  actionKey?: string | null;
  actionData?: string | null; // JSON string with parameters
  description?: string | null; // Deprecated: for backwards compatibility
  createdAt: Date | string;
  user?: {
    name: string;
    email: string;
  };
}

/**
 * Update Invoice Status Input
 */
export interface UpdateInvoiceStatusInput {
  status: InvoiceStatus;
}

/**
 * Helper Functions
 */

/**
 * Calculate due date from invoice date and payment terms
 */
export function calculateDueDate(invoiceDate: Date, paymentTermsDays: number): Date {
  const dueDate = new Date(invoiceDate);
  dueDate.setDate(dueDate.getDate() + paymentTermsDays);
  return dueDate;
}

/**
 * Calculate item amount
 */
export function calculateItemAmount(quantity: number | string, unitPrice: number | string): number {
  const qty = typeof quantity === "string" ? parseFloat(quantity) : quantity;
  const price = typeof unitPrice === "string" ? parseFloat(unitPrice) : unitPrice;
  return qty * price;
}

/**
 * Format currency
 */
export function formatCurrency(amount: number | string): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(num);
}

/**
 * Format date - Note: For component usage, prefer useDateFormat() hook
 * This function uses a fallback format and should only be used in non-component contexts
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("de-DE").format(d);
}

/**
 * Format date with time - Note: For component usage, prefer useDateFormat() hook
 * This function uses a fallback format and should only be used in non-component contexts
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}
